import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Loader2 } from "lucide-react";
import { compressImageForUpload } from "@/lib/driverImageUpload";
import { registerTsEmployee } from "@/lib/tsEmployeeApi";
import { deliveryJoinPath, staffBrandKicker, staffBrandTitle } from "@/lib/teamHub";

const PINK = "#EE2677";

function PhotoField({ label, preview, onPick, inputRef, capture = "environment" }) {
  return (
    <div className="ts-emp-field">
      <label>{label}</label>
      <div className="ts-emp-photo-row">
        {preview ? (
          <img src={preview} alt="" className="ts-emp-photo-preview" />
        ) : (
          <div className="ts-emp-photo-preview ts-emp-photo-empty">
            <Camera size={24} color={PINK} />
          </div>
        )}
        <button type="button" className="ts-emp-photo-btn" onClick={() => inputRef.current?.click()}>
          <Camera size={18} />
          {preview ? "Retake" : "Upload photo"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture={capture}
          style={{ display: "none" }}
          onChange={(e) => onPick(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

/** One-time staff access request — profile + Aadhar for Employee ID. */
export default function TsEmployeeSignup({
  initialPhone = "",
  phoneLocked = true,
  firebaseIdToken = "",
  onComplete,
  onCancel,
}) {
  const profileRef = useRef(null);
  const aadharRef = useRef(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState(initialPhone);
  const [upiId, setUpiId] = useState("");
  const [address, setAddress] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [aadharFile, setAadharFile] = useState(null);
  const [aadharPreview, setAadharPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onPickPhoto = async (file, setter, previewSetter) => {
    if (!file) return;
    try {
      const { blob } = await compressImageForUpload(file, { maxEdge: 640, webpQuality: 0.72, jpegQuality: 0.72 });
      setter(blob);
      previewSetter(URL.createObjectURL(blob));
      setErr("");
    } catch (e) {
      setErr(e.message || "Could not process photo");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      setErr("Add your profile photo");
      return;
    }
    if (!aadharFile) {
      setErr("Upload your Aadhar card photo");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const data = await registerTsEmployee({
        name,
        phone,
        address,
        upiId,
        photoFile,
        aadharFile,
        firebaseIdToken,
      });
      onComplete?.(data);
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ts-emp-signup">
      <style>{`
        .ts-emp-signup {
          min-height: 100dvh; background: #F5F2EA; color: #2D2D2D;
          padding: 12px 14px 28px; max-width: 480px; margin: 0 auto;
          font-family: system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
        }
        .ts-emp-signup * { box-sizing: border-box; }
        .ts-emp-brand { text-align: center; margin: 20px 0 18px; }
        .ts-emp-kicker {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
        }
        .ts-emp-title { font-size: 28px; font-weight: 900; color: ${PINK}; margin: 0; }
        .ts-emp-sub {
          font-size: 14px; color: rgba(45,45,45,0.65); margin: 8px 0 0; line-height: 1.45;
        }
        .ts-emp-rider-note {
          margin: 0 0 14px; padding: 12px 14px; border-radius: 14px;
          background: #FFF7ED; border: 1px solid #FDBA74;
          font-size: 13px; font-weight: 600; color: #9A3412; line-height: 1.45; text-align: center;
        }
        .ts-emp-rider-note a { color: #C2410C; font-weight: 800; }
        .ts-emp-card {
          background: #fff; border-radius: 20px; padding: 20px 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .ts-emp-heading { font-size: 20px; font-weight: 800; margin: 0 0 6px; }
        .ts-emp-field { margin-bottom: 12px; }
        .ts-emp-field label {
          display: block; font-size: 13px; font-weight: 700; margin-bottom: 6px;
        }
        .ts-emp-field input, .ts-emp-field textarea {
          width: 100%; border: 2px solid #E5E7EB; border-radius: 14px;
          padding: 12px 14px; font-size: 16px; background: #fff;
        }
        .ts-emp-field textarea { min-height: 72px; resize: vertical; }
        .ts-emp-photo-row { display: flex; align-items: center; gap: 14px; }
        .ts-emp-photo-preview {
          width: 72px; height: 72px; border-radius: 16px; object-fit: cover;
          border: 2px solid ${PINK}; background: #fff;
        }
        .ts-emp-photo-empty {
          display: flex; align-items: center; justify-content: center;
        }
        .ts-emp-photo-btn {
          flex: 1; border: 2px dashed #D1D5DB; border-radius: 14px; padding: 14px;
          background: #fff; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .ts-emp-primary {
          width: 100%; border: none; border-radius: 16px; padding: 16px;
          font-size: 17px; font-weight: 800; color: #fff; background: ${PINK};
          cursor: pointer; margin-top: 8px;
        }
        .ts-emp-primary:disabled { opacity: 0.6; cursor: wait; }
        .ts-emp-err { color: #B91C1C; font-size: 14px; font-weight: 600; margin: 8px 0; }
        .ts-emp-cancel {
          width: 100%; margin-top: 10px; border: none; background: transparent;
          color: rgba(45,45,45,0.55); font-size: 14px; font-weight: 700; cursor: pointer; padding: 8px;
        }
      `}</style>

      <div className="ts-emp-brand">
        <p className="ts-emp-kicker">{staffBrandKicker()}</p>
        <h1 className="ts-emp-title">{staffBrandTitle()}</h1>
        <p className="ts-emp-sub">Request staff access. Your manager will approve which apps you can use.</p>
      </div>

      <p className="ts-emp-rider-note">
        Delivery rider? Use the{" "}
        <Link to={deliveryJoinPath()}>Delivery app</Link>
        {" "}instead. This form is for kitchen, inventory, and office staff.
      </p>

      <div className="ts-emp-card">
        <h2 className="ts-emp-heading">Employee profile</h2>
        <p className="ts-emp-sub" style={{ marginBottom: 16 }}>
          Name, UPI, address, profile photo, and Aadhar — used for your Employee ID and payroll.
        </p>

        <form onSubmit={submit}>
          <div className="ts-emp-field">
            <label htmlFor="ts-emp-name">Full name</label>
            <input id="ts-emp-name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
          </div>
          <div className="ts-emp-field">
            <label htmlFor="ts-emp-phone">Mobile number</label>
            <input
              id="ts-emp-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              readOnly={phoneLocked}
              required
              autoComplete="tel"
            />
          </div>
          <div className="ts-emp-field">
            <label htmlFor="ts-emp-upi">Your UPI ID</label>
            <input
              id="ts-emp-upi"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value.trim())}
              required
              placeholder="name@paytm"
              inputMode="email"
            />
          </div>
          <div className="ts-emp-field">
            <label htmlFor="ts-emp-address">Home address</label>
            <textarea id="ts-emp-address" value={address} onChange={(e) => setAddress(e.target.value)} required />
          </div>

          <PhotoField
            label="Profile photo"
            preview={photoPreview}
            onPick={(f) => onPickPhoto(f, setPhotoFile, setPhotoPreview)}
            inputRef={profileRef}
            capture="user"
          />
          <PhotoField
            label="Aadhar card photo"
            preview={aadharPreview}
            onPick={(f) => onPickPhoto(f, setAadharFile, setAadharPreview)}
            inputRef={aadharRef}
          />

          {err ? <p className="ts-emp-err">{err}</p> : null}
          <button type="submit" className="ts-emp-primary" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" size={18} style={{ verticalAlign: "middle" }} /> : null}
            {busy ? " Submitting…" : "Submit for manager approval"}
          </button>
        </form>

        {onCancel ? (
          <button type="button" className="ts-emp-cancel" onClick={onCancel}>
            Back to login
          </button>
        ) : null}
      </div>
    </div>
  );
}
